import React, { FC } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useCurrentTheme } from "../../../../../theme/theme";

interface LabeledColor {
	label: string;
	color: string;
}

interface LabeledIcon {
	label: string;
	icon: React.ReactNode;
}

interface Props {
	colors?: LabeledColor[];
	icons?: LabeledIcon[];
	maxHeight: string | number;
	maxWidth: string | number;
}

/**
 * Renders a Legend over a Map, displaying colors and
 * icons with their labels.
 * NOTE: you MUST set the map's position as "relative"
 * to ensure a correct rendering.
 * NOTE: when creating icons, the suggested size is 16
 */
const Legend: FC<Props> = ({ colors, icons, maxHeight, maxWidth }) => {
	const { theme } = useCurrentTheme();

	const colorChips =
		colors &&
		colors?.map((labeledColor, index) => {
			return (
				<Stack direction={"row"} key={`legendColor${index}`} alignItems={"center"} spacing={1}>
					<Box
						key={`legendColor${index}Box`}
						sx={{
							width: "16px",
							height: "3px",
							backgroundColor: labeledColor.color,
							borderRadius: "2px",
						}}
					/>
					<Typography sx={{ fontSize: 8 }} variant={"inherit"}>
						{labeledColor.label.toUpperCase()}
					</Typography>
				</Stack>
			);
		});

	const iconChips =
		icons &&
		icons?.map((labeledIcon, index) => {
			return (
				<Stack direction={"row"} key={`legendIcon${index}`} alignItems={"center"} spacing={1}>
					{labeledIcon.icon}
					<Typography sx={{ fontSize: 8 }} variant={"inherit"}>
						{labeledIcon.label.toUpperCase()}
					</Typography>
				</Stack>
			);
		});

	return (
		<Box
			sx={{
				position: "absolute",
				left: 25,
				bottom: 25,
				zIndex: 1000,
				maxWidth: maxWidth,
				maxHeight: maxHeight,
				overflow: "scroll",
				msOverflowStyle: "none" /* Internet Explorer 10+ */,
				scrollbarWidth: "none" /* Firefox, Safari 18.2+, Chromium 121+ */,
				// opacity: 0.7,
			}}
			borderRadius={"5px"}
			bgcolor={theme.palette.background.paper + "fffCC"}
			padding={1}
		>
			<Stack alignItems={"left"}>
				<Stack direction={"column"} spacing={0.5}>
					{colorChips}
					{iconChips}
				</Stack>
			</Stack>
		</Box>
	);
};

export default Legend;
